import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { visibleItems } from "../data";

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

function clean(arr) {
  if (!arr) return [];
  return arr.filter((v) => v && v !== "none");
}

function pillColour(type) {
  const t = type.toLowerCase();
  if (
    ["character design", "illustration", "animation", "magazine"].some((k) =>
      t.includes(k)
    )
  )
    return "yellow";
  if (
    [
      "publication",
      "research",
      "presentation",
      "article",
      "desk-based research",
      "field study",
    ].some((k) => t.includes(k))
  )
    return "pink";
  if (
    [
      "infographic",
      "data visualisation",
      "visual journalism",
      "interactive dashboard",
      "web scrollytelling",
      "data scrollytelling",
    ].some((k) => t.includes(k))
  )
    return "teal";
  if (["client project"].some((k) => t.includes(k))) return "grey";
  return "orange";
}

function Lightbox({ src, alt, onClose }) {
  React.useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="lightbox" onClick={onClose}>
      <button className="lightbox__close" onClick={onClose}>✕</button>
      <img
        src={src}
        alt={alt}
        className="lightbox__img"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

function ProjectVisual({ item }) {
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  if (item.display === "iframe" && item.link) {
    return (
      <div className="ppage__embed-wrap">
        <iframe
          src={item.link}
          title={item.title}
          className="ppage__embed"
          allowFullScreen
        />
        <div className="ppage__embed-cta">
          <a href={item.link} target="_blank" rel="noopener noreferrer">
            {item.linkLabel || "Open project"}
          </a>
        </div>
      </div>
    );
  }

if (item.display === "link" && item.link) {
    return (
      <div className="ppage__link-wrap">
        <div className="ppage__embed-cta">
          <a href={item.link} target="_blank" rel="noopener noreferrer">
            {item.linkLabel || "Open project"}
          </a>
        </div>
      </div>
    );
  }

if (item.display === "media") {
    const src = item.media || item.thumbnail;
if (!src) {
  return (
    <>
      <div
        className="ppage__placeholder"
        style={{ "--accent": item.accentColor }}
      >
        <span className="ppage__placeholder-label">{item.title}</span>
      </div>
      {item.link && (
        <div className="ppage__embed-cta">
          <a href={item.link} target="_blank" rel="noopener noreferrer">
            {item.linkLabel || "Open project"}
          </a>
        </div>
      )}
    </>
  );
}
    return (
      <>
{item.mediaType === "video" ? (
  <>
    <video
      src={src}
      controls
      playsInline
      className={`ppage__media ppage__media--clickable ${
        item.thumbnailRatio && item.thumbnailRatio < 0.7
          ? "ppage__media--tall"
          : "ppage__media--wide"
      }`}
      onClick={() => setLightboxOpen(true)}
      title="Click to enlarge"
    />
    {lightboxOpen && (
      <div className="lightbox" onClick={() => setLightboxOpen(false)}>
        <button className="lightbox__close" onClick={() => setLightboxOpen(false)}>✕</button>
        <video
          src={src}
          controls
          playsInline
          autoPlay
          className="lightbox__img"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}
  </>
) : (
          <>
            <img
              src={src}
              alt={item.title}
              className={`ppage__media ppage__media--clickable ${
                item.thumbnailRatio && item.thumbnailRatio < 0.7
                  ? "ppage__media--tall"
                  : "ppage__media--wide"
              }`}
              onClick={() => setLightboxOpen(true)}
              title="Click to enlarge"
            />
            {lightboxOpen && (
              <Lightbox src={src} alt={item.title} onClose={() => setLightboxOpen(false)} />
            )}
          </>
        )}
        {item.link && (
          <div className="ppage__embed-cta">
            <a href={item.link} target="_blank" rel="noopener noreferrer">
              {item.linkLabel || "Open project"}
            </a>
          </div>
        )}
      </>
    );
  }

  return (
    <div
      className="ppage__placeholder"
      style={{ "--accent": item.accentColor }}
    >
      <span className="ppage__placeholder-label">{item.title}</span>
    </div>
  );
}

export default function ProjectPage() {
  const { slug } = useParams();
  const item = visibleItems.find((p) => p.slug === slug);

  if (!item) return <Navigate to="/portfolio" replace />;

  const idx = visibleItems.indexOf(item);
  const prev = visibleItems[idx - 1] || null;
  const next = visibleItems[idx + 1] || null;

  const designTools = clean(item.designTools);
  const graphicTools = clean(item.graphicTools);
  const analyticsTools = clean(item.analyticsTools);

  return (
    <div className="ppage">
      {/* Back */}
      <div className="ppage__back-wrap container">
        <Link to="/portfolio" className="ppage__back">
          ← All projects
        </Link>
      </div>

      {/* Header */}
      <header className="ppage__header container">
        <p className="ppage__date">{formatDate(item.date)}</p>
        <h1 className="ppage__title">{item.title}</h1>
        <p className="ppage__description">{item.description}</p>
      </header>

      {/* Type tags + visual */}
      <div className="ppage__visual-wrap container">
        <div className="ppage__types">
          {item.projectTypes.map((t) => (
            <span
              key={t}
              className={`ppage__type-pill ppage__type-pill--${pillColour(t)}`}
            >
              {t}
            </span>
          ))}
        </div>
        <ProjectVisual item={item} />
      </div>

      {/* Meta */}
      <div className="ppage__meta container">
        {designTools.length > 0 && (
          <div className="ppage__meta-block">
            <p className="ppage__meta-label">Design Tools</p>
            <div className="ppage__meta-tools">
              {designTools.map((t) => (
                <span key={t} className="ppage__meta-tool">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {graphicTools.length > 0 && (
          <div className="ppage__meta-block">
            <p className="ppage__meta-label">Graphic Tools</p>
            <div className="ppage__meta-tools">
              {graphicTools.map((t) => (
                <span key={t} className="ppage__meta-tool">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {analyticsTools.length > 0 && (
          <div className="ppage__meta-block">
            <p className="ppage__meta-label">Analytics & Research</p>
            <div className="ppage__meta-tools">
              {analyticsTools.map((t) => (
                <span key={t} className="ppage__meta-tool">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {item.sourceLabel && item.sourceLink && (
          <div className="ppage__meta-block">
            <p className="ppage__meta-label">Source</p>
            <a
              href={item.sourceLink}
              target="_blank"
              rel="noopener noreferrer"
              className="ppage__meta-link"
            >
              {item.sourceLabel}
            </a>
          </div>
        )}
      </div>

      {/* Prev / Next */}
      <nav className="ppage__pagination container">
        {prev ? (
          <Link
            to={`/portfolio/${prev.slug}`}
            className="ppage__nav ppage__nav--prev"
          >
            <span className="ppage__nav-dir">← Previous</span>
            <span className="ppage__nav-title">{prev.title}</span>
          </Link>
        ) : (
          <div />
        )}
        {next ? (
          <Link
            to={`/portfolio/${next.slug}`}
            className="ppage__nav ppage__nav--next"
          >
            <span className="ppage__nav-dir">Next →</span>
            <span className="ppage__nav-title">{next.title}</span>
          </Link>
        ) : (
          <div />
        )}
      </nav>
    </div>
  );
}
